import axios from "axios";
import { useSnackbar } from "notistack";

import {
  CreateUploadMutation,
  CreateUploadMutationVariables,
  UploadType,
  UploadVisibility,
} from "~frontend/__generated__/graphql";
import { MediaFieldValue } from "~frontend/components/forms/FormikMediaField";
import { CREATE_UPLOAD } from "~frontend/graphql/upload";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import useMutation from "~frontend/util/useMutation";

export function useMediaUpload(): {
  uploadMedia: (
    media: MediaFieldValue
  ) => Promise<{ id: string; url: string | null } | undefined | null>;
} {
  const { enqueueSnackbar } = useSnackbar();
  const { subaccount } = useSubaccount()!;

  const [createUpload] = useMutation<
    CreateUploadMutation,
    CreateUploadMutationVariables
  >(CREATE_UPLOAD, {
    onErrorSnackbarMessage: "Error uploading CSV",
  });

  const uploadMedia = async (media: MediaFieldValue) => {
    if (media?.upload) {
      return { id: media.upload.id, url: media.upload.publicGetUrl ?? null };
    } else if (media?.file) {
      const { errors, data: createUploadData } = await createUpload({
        variables: {
          input: {
            subaccountId: subaccount.id,
            type: UploadType.MmsMedia,
            contentType: media.file.type,
            fileName: media.file.name,
            visibility: UploadVisibility.Public,
          },
        },
      });

      if (errors) {
        enqueueSnackbar("Error uploading media", { variant: "error" });
        return;
      }

      try {
        await axios.put(
          createUploadData!.createUpload.presignedPutUrl,
          media.file,
          {
            headers: { "Content-Type": media.file.type },
          }
        );
      } catch (e) {
        console.error(e);
        enqueueSnackbar("Failed to upload media", { variant: "error" });
        return;
      }

      return {
        id: createUploadData!.createUpload.id,
        url: createUploadData!.createUpload.publicGetUrl ?? null,
      };
    }

    // Explicitly unset the mediaUploadId
    return null;
  };
  return { uploadMedia };
}
